import { City } from '@app/types';
import { Autocomplete } from '@material-ui/lab';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import allCities from '@app/data/cities.json';
import { searchCityByName } from './helpers';
import classes from './styles.module.css';
import cls from 'classnames';

type Props = {
  defaultValue: City | null;
  city: City | null;
  onSelectCity: (city: City | null) => void;
  ErrorMessageComponent: ReactNode | undefined;
  onClickWhenDisabled: () => void;
  inputClassName: string;
  disabled: boolean;
};

export default function CitySelector({
  defaultValue,
  city,
  onSelectCity,
  onClickWhenDisabled,
  ErrorMessageComponent,
  inputClassName,
  disabled,
}: Props) {
  const [inputText, setInputText] = useState('');
  const [inputIsFocused, setInputIsFocused] = useState(false);
  const [cities, setCities] = useState<City[]>([]);
  const [isFiltering, setIsFiltering] = useState(false);

  const searchTimeout = useRef<number>();
  useEffect(() => {
    if (inputText.trim() === '' || !inputIsFocused) {
      window.clearTimeout(searchTimeout.current);
      setCities([]);
      setIsFiltering(false);
      return;
    }

    window.clearTimeout(searchTimeout.current);
    setIsFiltering(true);
    searchTimeout.current = window.setTimeout(() => {
      const citiesFiltered = searchCityByName(inputText.trim(), '');
      setCities(citiesFiltered);
      setIsFiltering(false);
    }, 500);
  }, [inputText, inputIsFocused]);

  function getNoOptionsText() {
    if (inputText.trim() === '' || !inputIsFocused) return null;
    if (isFiltering) return 'Pesquisando cidade...';
    return 'Cidade não encontrada';
  }

  function handleContainerClick() {
    if (disabled) {
      onClickWhenDisabled();
    }
  }

  return (
    <div onClick={handleContainerClick} className={cls(disabled && classes.clickable)}>
      <Autocomplete
        defaultValue={defaultValue}
        value={city}
        options={allCities}
        getOptionLabel={(option) => `${option.Nom_Cidade}, ${option.Sigla_UF}`}
        getOptionSelected={(option) => city?.Cod_Cidade === option.Cod_Cidade}
        ListboxProps={{
          style: { backgroundColor: '#eee' },
        }}
        noOptionsText={getNoOptionsText()}
        filterOptions={() => {
          if (isFiltering) return [];
          return cities;
        }}
        classes={{
          noOptions: cls(classes.noOptions, getNoOptionsText() === null && classes.hidden),
        }}
        blurOnSelect
        openOnFocus
        autoHighlight
        onFocus={() => setInputIsFocused(true)}
        onBlur={() => setInputIsFocused(false)}
        onInputChange={(_, value) => setInputText(value)}
        onChange={(_, newValue) => {
          if (!newValue) return;
          onSelectCity(newValue);
        }}
        disabled={disabled}
        renderOption={(option) => (
          <div className={classes.option}>
            <div className={classes.square} />
            <span className={classes.optionLabel}>{`${option.Nom_Cidade}, ${option.Sigla_UF}`}</span>
          </div>
        )}
        renderInput={(params) => (
          <div ref={params.InputProps.ref}>
            <input
              {...params.inputProps}
              className={inputClassName}
              placeholder="Cidade"
              style={{ width: '100%' }}
              data-cy="user-informations-city-input"
            />
            {ErrorMessageComponent && ErrorMessageComponent}
          </div>
        )}
      />
    </div>
  );
}
